import { useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { todosAPI } from '../api/endpoints';
import type { Reminder, TodoDueHorizon, TodoSearchParams, TodoType } from '../types';

const DEFAULT_PAGE_SIZE = 100;

/** Shown by default: done and dismissed todos are opt-in through the Status column filter. */
export const DEFAULT_STATUSES: Reminder['status'][] = ['pending'];

/**
 * Shown by default: what needs doing now. Todos due later are one tick away in the Due filter,
 * and the dropdown's "Later (n)" count is what makes them discoverable.
 */
export const DEFAULT_DUE: TodoDueHorizon[] = ['overdue', 'today'];

/** Parse a comma-separated multi-select back out of the URL. */
function parseList<T extends string>(raw: string | null, allowed: readonly T[]): T[] {
  if (!raw) return [];
  return raw
    .split(',')
    .map((v) => v.trim())
    .filter((v): v is T => (allowed as readonly string[]).includes(v));
}

const ALL_STATUSES: Reminder['status'][] = ['pending', 'done', 'dismissed'];
const ALL_TYPES: TodoType[] = ['remind', 'contact', 'task'];
const ALL_DUE: TodoDueHorizon[] = ['overdue', 'today', 'later'];

/**
 * URL-synced state for the todo page's paginated table — the same pattern as
 * `useVenueSearch`: every filter, the sort and the page live in the query string, so a view is
 * linkable and survives a reload, and `placeholderData` keeps the old page on screen while the
 * next one loads instead of flashing a skeleton.
 *
 * The status filter defaults to pending only. That default is *implicit* (no `status` param):
 * the empty selection is written as the `none` sentinel instead, so "untick everything" is not
 * silently turned back into the default on the next read.
 */
export function useTodoSearch() {
  const [searchParams, setSearchParams] = useSearchParams();

  const rawStatus = searchParams.get('status');
  const statuses = rawStatus === null ? DEFAULT_STATUSES : parseList(rawStatus, ALL_STATUSES);
  const rawDue = searchParams.get('due');
  const due = rawDue === null ? DEFAULT_DUE : parseList(rawDue, ALL_DUE);
  const types = parseList(searchParams.get('type'), ALL_TYPES);

  const params: TodoSearchParams = {
    q: searchParams.get('q')?.trim() || undefined,
    status: rawStatus === null ? DEFAULT_STATUSES.join(',') : rawStatus,
    type: types.length > 0 ? types.join(',') : undefined,
    due: rawDue === null ? DEFAULT_DUE.join(',') : rawDue,
    sort_by: searchParams.get('sort_by') || undefined,
    sort_order: (searchParams.get('sort_order') as 'asc' | 'desc') || undefined,
    page: Number(searchParams.get('page')) || 1,
    page_size: Number(searchParams.get('page_size')) || DEFAULT_PAGE_SIZE,
  };

  const query = useQuery({
    queryKey: ['todos', 'search', params],
    queryFn: async ({ signal }) => {
      const response = await todosAPI.list(params, signal);
      return response.data;
    },
    placeholderData: (previousData) => previousData,
    staleTime: 30_000,
  });

  const updateParams = (updates: Record<string, string | number | undefined>) => {
    const newParams = new URLSearchParams(searchParams);
    Object.entries(updates).forEach(([key, value]) => {
      if (value === undefined || value === '' || value === null) {
        newParams.delete(key);
      } else {
        newParams.set(key, String(value));
      }
    });
    setSearchParams(newParams);
  };

  // Trim before it reaches the URL: surrounding whitespace is never part of the term, and
  // keeping it would re-key the query (and refetch) on a stray trailing space.
  const setSearch = (q: string) => updateParams({ q: q.trim() || undefined, page: 1 });

  const setStatuses = (next: Reminder['status'][]) =>
    // 'none' rather than an absent param: absent means "the default", which is not what an
    // empty selection asks for.
    updateParams({ status: next.length > 0 ? next.join(',') : 'none', page: 1 });

  const setTypes = (next: TodoType[]) =>
    updateParams({ type: next.length > 0 ? next.join(',') : undefined, page: 1 });

  const setDue = (next: TodoDueHorizon[]) =>
    // Same 'none' sentinel as the status filter: an absent param means the default, which is
    // not what unticking every horizon asks for.
    updateParams({ due: next.length > 0 ? next.join(',') : 'none', page: 1 });

  const setSort = (sort_by: string, sort_order: 'asc' | 'desc' | null) =>
    updateParams({
      sort_by: sort_order ? sort_by : undefined,
      sort_order: sort_order ?? undefined,
    });

  const setPage = (page: number) => updateParams({ page });

  const resetFilters = () => setSearchParams(new URLSearchParams());

  const isFiltered =
    params.q !== undefined ||
    params.type !== undefined ||
    (rawStatus !== null && rawStatus !== DEFAULT_STATUSES.join(',')) ||
    (rawDue !== null && rawDue !== DEFAULT_DUE.join(','));

  return {
    todos: query.data?.items ?? [],
    pagination: query.data
      ? {
          total: query.data.total,
          page: query.data.page,
          pageSize: query.data.page_size,
          totalPages: query.data.total_pages,
          hasNext: query.data.has_next,
          hasPrev: query.data.has_prev,
        }
      : null,
    isLoading: query.isLoading,
    isFetching: query.isFetching,
    error: query.error,
    params,
    statuses,
    types,
    due,
    isFiltered,
    setSearch,
    setStatuses,
    setTypes,
    setDue,
    setSort,
    setPage,
    resetFilters,
    refetch: query.refetch,
  };
}
